import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaDoceM(props, ref) {
  const { content = {}, logoSrc, bgColor } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const data = Object.assign({}, content, opts);

      const containerWidth = wrapper ? wrapper.clientWidth : 900;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // text defaults
      const title = data.title || opts.title || '';
      const subTitle = data.subTitle || opts.subTitle || '';

      // layout basics
      const padding = 28;
      let y = padding;
      const usableW = width - padding * 2;

      // Title square and title
      const titleFontSize = opts.titleFontSize || 50;
      const squareSize =
        typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 2);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 12;
      const squareText = opts.titleSquareText || '2.1';

      // draw square at left
      const squareX = padding;
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      // square number/text
      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // draw title to the right of square and subtitle inside the square height
      const titleX = squareX + squareSize + squareGap;
      if (title || subTitle) {
        const titleMaxW = Math.max(0, usableW - (squareSize + squareGap));
        const titleLines = title ? String(title).split('\n') : [];
        const subLines = subTitle ? String(subTitle).split('\n') : [];

        const baseTitleFontSize = titleFontSize;
        const baseSubSize = opts.subTitleFontSize || 40;
        const baseTitleLineH = Math.round(baseTitleFontSize * 1.2);
        const baseSubLineH = Math.round(baseSubSize);
        const titleSubtitleGap = typeof opts.titleSubtitleGap === 'number' ? opts.titleSubtitleGap : 12;

        let totalTextH =
          (titleLines.length ? titleLines.length * baseTitleLineH : 0) +
          (subLines.length ? titleSubtitleGap + subLines.length * baseSubLineH : 0);

        let titleFontToUse = baseTitleFontSize;
        let subFontToUse = baseSubSize;
        let titleLineH = baseTitleLineH;
        let subLineH = baseSubLineH;

        if (totalTextH > squareSize) {
          const scale = squareSize / totalTextH;
          titleFontToUse = Math.max(10, Math.floor(baseTitleFontSize * scale));
          subFontToUse = Math.max(8, Math.floor(baseSubSize * scale));
          titleLineH = Math.round(titleFontToUse * 1.2);
          subLineH = Math.max(10, Math.round(subFontToUse));
          totalTextH =
            (titleLines.length ? titleLines.length * titleLineH : 0) +
            (subLines.length ? titleSubtitleGap + subLines.length * subLineH : 0);
        }

        let textStartY = y + Math.round((squareSize - totalTextH) / 2);

        if (titleLines.length) {
          ctx.fillStyle = opts.titleColor || '#0f172a';
          ctx.font = `bold ${titleFontToUse}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          let ty = textStartY;
          for (let ln of titleLines) {
            ctx.fillText(ln, titleX, ty);
            ty += titleLineH;
          }

          if (subLines.length) {
            let sy = textStartY + titleLines.length * titleLineH + titleSubtitleGap;
            ctx.fillStyle = opts.subTitleColor || '#374151';
            ctx.font = `${subFontToUse}px Inter, Arial, sans-serif`;
            ctx.textBaseline = 'top';
            for (let ln of subLines) {
              ctx.fillText(ln, titleX, sy);
              sy += subLineH;
            }
          }
        } else {
          // only subtitle
          if (subLines.length) {
            let sy = y + Math.round((squareSize - subLines.length * baseSubLineH) / 2);
            ctx.fillStyle = opts.subTitleColor || '#374151';
            ctx.font = `${baseSubSize}px Inter, Arial, sans-serif`;
            ctx.textBaseline = 'top';
            for (let ln of subLines) {
              ctx.fillText(ln, titleX, sy);
              sy += baseSubLineH;
            }
          }
        }

        // advance below the square
        y = y + squareSize;
      } else {
        y = y + squareSize;
      }
      y += 40; // smaller gap below title
      // compute chart area used for the map (below the title square)
      // leave a smaller bottom margin so the map can expand
      const pageChartsBottom = height - padding - 80;
      const maxAllowedBottom = typeof opts.maxChartsBottom === 'number' ? opts.maxChartsBottom : 800;
      const chartsBottom = Math.min(pageChartsBottom, maxAllowedBottom);
      const chartsTop = y;

      // allow the map height to grow up to 800px but keep a sensible minimum
      const chartsH = Math.min(800, Math.max(200, chartsBottom - chartsTop));
      const chartsW = usableW;

      // draw map (if provided) and then draw logo + resolve
      const mapSrc = opts.mapaSrc || data.mapaSrc || props.mapaSrc;

      const finish = () => resolve({ canvas, width, height });

      const drawLogoAndFinish = () => {
        const mainLogoSrc = opts.logoSrc || data.logoSrc || logoSrc;
        if (mainLogoSrc) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const maxW = Math.min(200, Math.round(width * 0.12));
              const w = Math.min(maxW, img.width);
              const h = (img.height / img.width) * w;
              const lx = width - padding - w;
              const ly = height - padding - h;
              ctx.drawImage(img, lx, ly, w, h);
            } catch (e) {
              /* ignore */
            }
            finish();
          };
          img.onerror = () => finish();
          img.src = mainLogoSrc;
        } else {
          finish();
        }
      };

      if (mapSrc) {
        const mimg = new Image();
        mimg.crossOrigin = 'anonymous';
        mimg.onload = () => {
          try {
            // available area for the map
            const mx = padding;
            const my = chartsTop;
            const availableW = chartsW;
            const availableH = chartsH;

            // scale to fit within availableW x availableH preserving aspect ratio
            let drawW = availableW;
            let drawH = Math.round((mimg.height / mimg.width) * drawW);
            if (drawH > availableH) {
              drawH = availableH;
              drawW = Math.round((mimg.width / mimg.height) * drawH);
            }

            const dx = mx + Math.round((availableW - drawW) / 2);
            const dy = my + Math.round((availableH - drawH) / 2);
            ctx.drawImage(mimg, dx, dy, drawW, drawH);
          } catch (e) {
            /* ignore drawing errors */
          }
          drawLogoAndFinish();
        };
        mimg.onerror = () => drawLogoAndFinish();
        mimg.src = mapSrc;
      } else {
        drawLogoAndFinish();
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_ocho_${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
  };

  useImperativeHandle(
    ref,
    () => ({
      draw,
      download,
      getDataUrl: () => {
        const canvas = canvasRef.current;
        return canvas ? canvas.toDataURL('image/png') : null;
      },
    }),
    [draw]
  );

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});